import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BaseService } from "../services/base-service";
import { URLS } from "../services/app-urls";
import { RFCExecution } from "../models/general/import";
import { RfcType } from "../types";
import { SapImportHealth } from "../models/general/sap-integration-health";
import { buildPriceDetailsUrl, mapExecutionStatus, PRICE_LOAD_ID } from "../utils/sap-integration-health-utils";

const POLL_INTERVAL_MS = 15_000;

interface SapLoadConfig {
  id: string;
  nameKey: string;
  rfcType: RfcType;
  detailsUrl: string;
}

const SAP_LOADS: SapLoadConfig[] = [
  { id: "bom", nameKey: "bom", rfcType: RfcType.BOM, detailsUrl: "/general/import-sap?tab=bom" },
  { id: "stock_and_po", nameKey: "stock_and_po", rfcType: RfcType.STOCK_AND_PO, detailsUrl: "/general/import-sap?tab=stock" },
  { id: "forecast", nameKey: "forecast", rfcType: RfcType.FORECAST, detailsUrl: "/general/import-sap?tab=forecast" },
  { id: PRICE_LOAD_ID, nameKey: "price_per_pn", rfcType: RfcType.PRICE, detailsUrl: buildPriceDetailsUrl() },
];

const fetchLoadHealth = async (load: SapLoadConfig): Promise<SapImportHealth> => {
  const service = new BaseService<RFCExecution>(URLS.RFC_EXECUTIONS);
  service.clearParameters();
  service.addParameter("rfc_type", load.rfcType);
  service.addParameter("ordering", "-created_at");
  service.addParameter("limit", 1);

  const response = await service.getAllPaginate();
  const latest = response.data.results?.[0];

  if (!latest) {
    return {
      id: load.id,
      nameKey: load.nameKey,
      status: "not_run",
      startedAt: null,
      finishedAt: null,
      durationSeconds: null,
      errorMessage: null,
      detailsUrl: load.detailsUrl,
    };
  }

  return {
    id: load.id,
    nameKey: load.nameKey,
    status: mapExecutionStatus(latest.status),
    startedAt: latest.started_at ?? null,
    finishedAt: latest.finished_at ?? null,
    durationSeconds: latest.duration_seconds ?? null,
    errorMessage: latest.error_message ?? null,
    detailsUrl: load.detailsUrl,
  };
};

export function useSapIntegrationHealth(analysisId?: number | null) {
  const [rawImports, setRawImports] = useState<SapImportHealth[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    setIsError(false);

    Promise.all(SAP_LOADS.map(fetchLoadHealth))
      .then(results => {
        if (!active || !isMountedRef.current) return;
        setRawImports(results);
        setIsLoading(false);
      })
      .catch(() => {
        if (!active || !isMountedRef.current) return;
        setIsError(true);
        setIsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [reloadKey]);

  const refetch = useCallback(() => {
    setReloadKey(key => key + 1);
  }, []);

  // Derived, not part of the fetch cycle: swapping the Dashboard's selected analysis only needs to
  // update the Price card's destination, not re-fetch the 4 loads.
  const imports = useMemo(
    () =>
      rawImports.map(item =>
        item.id === PRICE_LOAD_ID ? { ...item, detailsUrl: buildPriceDetailsUrl(analysisId) } : item
      ),
    [rawImports, analysisId]
  );

  const hasProcessing = imports.some(item => item.status === "processing");

  useEffect(() => {
    if (!hasProcessing) return;

    const interval = setInterval(() => {
      refetch();
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [hasProcessing, refetch]);

  return { imports, isLoading, isError, refetch };
}
